import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import type { Session } from '@/lib/types';
import { SessionCard } from '@/ui/SessionCard';
import { SessionDetailsDialog } from '@/ui/SessionDetailsDialog';

export function AgendaPage() {
  const [busySessionId, setBusySessionId] = useState<number | null>(null);
  const [openSlots, setOpenSlots] = useState<Record<number, boolean>>({});
  const [detailSession, setDetailSession] = useState<Session | null>(null);
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
  });

  const agendaQuery = useQuery({
    queryKey: ['agenda'],
    queryFn: () => api.agenda(),
  });

  useEffect(() => {
    const slots = agendaQuery.data?.slots ?? [];

    if (slots.length === 0) {
      return;
    }

    setOpenSlots((current) => {
      const next: Record<number, boolean> = {};
      let changed = false;

      slots.forEach((slot, index) => {
        if (slot.id in current) {
          next[slot.id] = current[slot.id];
          return;
        }

        next[slot.id] = index === 0 || slot.selectedSessionId !== null;
        changed = true;
      });

      if (Object.keys(current).length !== Object.keys(next).length) {
        changed = true;
      }

      return changed ? next : current;
    });
  }, [agendaQuery.data]);

  const bookMutation = useMutation({
    mutationFn: async (sessionId: number) => {
      setBusySessionId(sessionId);
      return api.bookSession(sessionId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agenda'] }),
        queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
        queryClient.invalidateQueries({ queryKey: ['me'] }),
      ]);
    },
    onSettled: () => setBusySessionId(null),
  });

  const cancelMutation = useMutation({
    mutationFn: async (sessionId: number) => {
      setBusySessionId(sessionId);
      return api.cancelSession(sessionId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agenda'] }),
        queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
      ]);
    },
    onSettled: () => setBusySessionId(null),
  });

  const infoMessage = useMemo(() => {
    if (meQuery.error instanceof ApiError && meQuery.error.status === 401) {
      return 'Die Agenda ist offen sichtbar. Zum Anmelden brauchst du Nachname und Zugangscode.';
    }

    if (bookMutation.error instanceof ApiError) {
      return bookMutation.error.message;
    }

    if (cancelMutation.error instanceof ApiError) {
      return cancelMutation.error.message;
    }

    return null;
  }, [meQuery.error, bookMutation.error, cancelMutation.error]);

  function toggleSlot(slotId: number) {
    setOpenSlots((current) => ({
      ...current,
      [slotId]: !current[slotId],
    }));
  }

  return (
    <div className="stack stack--xl">
      <section className="panel">
        <div className="panel__header">
          <span className="eyebrow">Find your tracks</span>
          <h2>Agenda in 5 Slots</h2>
          <p>Pro Zeitslot ist genau eine Anmeldung möglich. Freie Slots dürfen frei bleiben.</p>
        </div>

        {infoMessage ? <p className="notice">{infoMessage}</p> : null}

        {agendaQuery.data?.days.length ? (
          <div className="single-day-banner">
            <strong>{agendaQuery.data.days[0].label}</strong>
            <span>{agendaQuery.data.days[0].eventDate}</span>
          </div>
        ) : null}
      </section>

      {agendaQuery.isLoading ? <p className="notice">Agenda wird geladen…</p> : null}

      {agendaQuery.data?.slots.map((slot) => {
        const isOpen = openSlots[slot.id] ?? false;

        return (
          <section className={`slot-panel ${isOpen ? 'slot-panel--open' : 'slot-panel--collapsed'}`} key={slot.id}>
            <button className="slot-panel__toggle" type="button" onClick={() => toggleSlot(slot.id)}>
              <div>
                <span className="eyebrow">Slot</span>
                <h3>
                  {slot.startTime} – {slot.endTime}
                </h3>
                <p>{slot.selectedSessionId ? 'Du bist in diesem Slot bereits angemeldet.' : 'Dieser Slot ist noch offen.'}</p>
              </div>

              <div className="slot-panel__summary">
                <span className={`status-pill ${slot.selectedSessionId ? 'status-pill--active' : 'status-pill--idle'}`}>
                  {slot.selectedSessionId ? 'Angemeldet' : 'Offen'}
                </span>
                <span className="slot-panel__count">{slot.sessions.length} Sessions</span>
                <span className="slot-panel__chevron" aria-hidden="true">
                  {isOpen ? '−' : '+'}
                </span>
              </div>
            </button>

            {isOpen ? (
              <div className="session-grid">
                {slot.sessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    canBook={meQuery.isSuccess}
                    busySessionId={busySessionId}
                    onOpenDetails={setDetailSession}
                    onBook={(sessionId) => bookMutation.mutate(sessionId)}
                    onCancel={(sessionId) => cancelMutation.mutate(sessionId)}
                  />
                ))}
              </div>
            ) : null}
          </section>
        );
      })}

      <SessionDetailsDialog session={detailSession} onClose={() => setDetailSession(null)} />
    </div>
  );
}
